import { Center, Spinner, Text } from '@chakra-ui/react';
import useQueryParam from 'hooks/shared/useQueryParam';
import { FC } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useFetchPeAssetsQuery } from 'rtkQuery/sharespro/queries/peService';

import ErrorContainer from 'presentation/components/shared/ErrorContainer';
import ActivesPageLayout from 'presentation/layout/modules/actives/ActivesPageLayout';
import GridHeading from 'presentation/components/shared/grid/GridHeading';
import AppCard from 'presentation/components/shared/card/AppCard';
import {
  isValidPeAssetState,
  PeAssetState,
} from 'enums/modules/pe/peAssetState';
import ButtonTabs from 'presentation/components/shared/tabs/ButtonTabs';
import SidebarRequestCard from 'presentation/components/modules/pe/shared/SidebarRequestCard';
import PeOffersGrid from 'presentation/components/modules/pe/offersGrid/PeOffersGrid';
import { useNavigate } from 'react-router-dom';
import AppRoute from 'router/AppRoute';

const PeDealsPage: FC = () => {
  const { t } = useTranslation();

  const [dealState, setDealState] = useQueryParam<PeAssetState>('state', {
    ifAbsent: PeAssetState.Active,
    provider: (val) => (isValidPeAssetState(+val) ? +val : PeAssetState.Active),
  });

  const { isFetching, data, error, refetch } = useFetchPeAssetsQuery({
    state: dealState,
  });

  const navigate = useNavigate();

  const peDealStates = [PeAssetState.Active, PeAssetState.Archived];

  const peDealStateText = {
    [PeAssetState.Active]: t('peDealStateActive'),
    [PeAssetState.Archived]: t('peDealStateArchived'),
  };

  let content;
  let wrapByCard = true;
  if (data) {
    if (data.items.length > 0) {
      content = (
        <PeOffersGrid
          offers={data.items}
          onCardClick={(asset) => navigate(AppRoute.peDealDetail(asset.id))}
          onActionClick={(asset) => navigate(AppRoute.peDealDetail(asset.id))}
        />
      );
      wrapByCard = false;
    } else {
      content = <Text>{t('listIsEmpty')}</Text>;
    }
  } else if (isFetching) {
    content = <Spinner />;
  } else if (error) {
    content = (
      <ErrorContainer
        error={error}
        onRetry={() => {
          refetch();
        }}
      />
    );
  } else {
    content = <Spinner />;
  }

  if (wrapByCard) {
    content = (
      <AppCard p="20px">
        <Center>{content}</Center>
      </AppCard>
    );
  }

  return (
    <ActivesPageLayout sidebarContent={<SidebarRequestCard />}>
      <Helmet>
        <title>{t('myDealsLabel')}</title>
      </Helmet>
      <GridHeading>{t('myDealsLabel')}</GridHeading>
      <ButtonTabs
        mb="20px"
        onChange={(index) => setDealState(peDealStates[index])}
        index={peDealStates.indexOf(dealState)}
        items={peDealStates.map((state) => peDealStateText[state])}
      />
      {content}
    </ActivesPageLayout>
  );
};

export default PeDealsPage;
